import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ImagingIntegrationPanel = ({ linkedImaging, planId }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [showAnnotations, setShowAnnotations] = useState(true);

  const imagingLibrary = [
    {
      id: 1,
      type: 'Bitewing',
      category: 'X-ray',
      toothNumber: '#14-15',
      date: '2026-01-15',
      provider: 'Dr. Chen',
      status: 'reviewed',
      thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_dental_xray_1.png',
      thumbnailAlt: 'Right posterior bitewing X-ray showing upper and lower molars with clear interproximal spaces',
      annotations: [
        { id: 1, type: 'arrow', text: 'Caries detected - occlusal surface', position: { x: 120, y: 80 } },
        { id: 2, type: 'circle', text: 'Crown margin - good seal', position: { x: 200, y: 150 } }
      ],
      linkedToProcedures: ['D2391', 'D2740']
    },
    {
      id: 7,
      type: 'Panoramic',
      category: 'X-ray',
      toothNumber: 'Full Mouth',
      date: '2026-01-05',
      provider: 'Dr. Chen',
      status: 'reviewed',
      thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_dental_panoramic_1.png',
      thumbnailAlt: 'Panoramic X-ray showing complete dental arches and TMJ joints',
      annotations: [
        { id: 1, type: 'marker', text: 'Missing tooth #30 - implant candidate', position: { x: 350, y: 200 } },
        { id: 2, type: 'marker', text: 'Adequate bone density for implant', position: { x: 360, y: 220 } }
      ],
      linkedToProcedures: ['D6010', 'D6065']
    },
    {
      id: 15,
      type: 'CBCT',
      category: 'CT Scan',
      toothNumber: '#30 site',
      date: '2026-01-08',
      provider: 'Dr. Rodriguez',
      status: 'reviewed',
      thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_dental_ct_1.png',
      thumbnailAlt: 'Cone beam CT scan showing 3D bone structure for implant planning',
      annotations: [
        { id: 1, type: 'measurement', text: 'Bone height: 12mm - sufficient', position: { x: 180, y: 100 } },
        { id: 2, type: 'measurement', text: 'Bone width: 8mm - adequate', position: { x: 180, y: 130 } }
      ],
      linkedToProcedures: ['D6010', 'D6104']
    },
    {
      id: 22,
      type: 'Intraoral Photo',
      category: 'Clinical Photo',
      toothNumber: '#14',
      date: '2026-01-20',
      provider: 'Dr. Chen',
      status: 'reviewed',
      thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_intraoral_restoration.png',
      thumbnailAlt: 'Intraoral photo showing completed composite restoration on posterior tooth',
      annotations: [
        { id: 1, type: 'note', text: 'Composite restoration completed - excellent marginal seal', position: { x: 150, y: 120 } }
      ],
      linkedToProcedures: ['D2391']
    }
  ];

  const handleImageSelect = (image) => {
    setSelectedImage(image);
  };

  const handleLinkImage = (imageId) => {
    console.log('Linking image to treatment plan:', imageId, planId);
  };

  return (
    <div className="space-y-6">
      {/* Imaging Library */}
      <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="Image" size={20} className="text-primary" />
            Imaging Library
          </h3>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" iconName="Filter">
              Filter
            </Button>
            <Button variant="outline" size="sm" iconName="Upload">
              Upload
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {imagingLibrary?.map((image) => (
            <div
              key={image?.id}
              onClick={() => handleImageSelect(image)}
              className={`bg-background rounded-lg border-2 p-3 cursor-pointer transition-smooth ${
                selectedImage?.id === image?.id
                  ? 'border-primary shadow-elevation-1'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <div className="aspect-video bg-muted rounded-lg mb-3 overflow-hidden">
                <img
                  src={image?.thumbnail}
                  alt={image?.thumbnailAlt}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-foreground">{image?.type}</h4>
                  <span className="text-xs px-2 py-1 bg-success/10 text-success rounded">
                    {image?.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="MapPin" size={10} />
                    {image?.toothNumber}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" size={10} />
                    {image?.date}
                  </span>
                </div>
                {image?.annotations?.length > 0 && (
                  <div className="flex items-center gap-1 text-xs text-primary">
                    <Icon name="MessageSquare" size={12} />
                    <span>{image?.annotations?.length} annotations</span>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <Button variant="ghost" size="xs" iconName="Link" onClick={(e) => { e?.stopPropagation(); handleLinkImage(image?.id); }}>
                    Link to Plan
                  </Button>
                  <Button variant="ghost" size="xs" iconName="Eye">
                    View
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Selected Image Detail View */}
      {selectedImage && (
        <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Image Details & Annotations</h3>
            <div className="flex items-center gap-2">
              <Button
                variant={showAnnotations ? 'default' : 'outline'}
                size="sm"
                iconName="MessageSquare"
                onClick={() => setShowAnnotations(!showAnnotations)}
              >
                Annotations
              </Button>
              <button onClick={() => setSelectedImage(null)}>
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Image Viewer */}
            <div className="space-y-3">
              <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                <img
                  src={selectedImage?.thumbnail}
                  alt={selectedImage?.thumbnailAlt}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" iconName="ZoomIn">
                  Zoom
                </Button>
                <Button variant="outline" size="sm" iconName="RotateCw">
                  Rotate
                </Button>
                <Button variant="outline" size="sm" iconName="Maximize">
                  Fullscreen
                </Button>
              </div>
            </div>

            {/* Annotations & Metadata */}
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-foreground mb-3">Image Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Type:</span>
                    <span className="font-medium text-foreground">{selectedImage?.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Category:</span>
                    <span className="font-medium text-foreground">{selectedImage?.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Location:</span>
                    <span className="font-medium text-foreground">{selectedImage?.toothNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date:</span>
                    <span className="font-medium text-foreground">{selectedImage?.date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Provider:</span>
                    <span className="font-medium text-foreground">{selectedImage?.provider}</span>
                  </div>
                </div>
              </div>

              {showAnnotations && selectedImage?.annotations?.length > 0 && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-foreground mb-3">Annotations</h4>
                  <div className="space-y-2">
                    {selectedImage?.annotations?.map((annotation) => (
                      <div key={annotation?.id} className="flex items-start gap-2 p-2 bg-background rounded">
                        <Icon name="MessageSquare" size={14} className="text-primary mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs font-medium text-foreground">{annotation?.type?.toUpperCase()}</p>
                          <p className="text-xs text-muted-foreground">{annotation?.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="w-full mt-3" iconName="Plus">
                    Add Annotation
                  </Button>
                </div>
              )}

              {selectedImage?.linkedToProcedures?.length > 0 && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-foreground mb-3">Linked Procedures</h4>
                  <div className="space-y-1">
                    {selectedImage?.linkedToProcedures?.map((code, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="Link" size={12} />
                        <span>{code}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagingIntegrationPanel;